const DEFAULT_CRAWLER_HEADING_LENGTH = 60;
const MIN_DERIVED_HEADING_LENGTH = 20;
const MIN_FALLBACK_HEADING_LENGTH = 14;

const CLAUSE_BOUNDARY_PATTERNS = [
  /\s+that\s+/i,
  /\s+who\s+/i,
  /\s+which\s+/i,
  /\s+for\s+teams\s+/i,
  /\s+for\s+clients\s+/i,
  /\s+without\s+/i,
  /\s+que\s+/i,
  /\s+qui\s+/i,
  /\s+die\s+/i,
] as const;

const TITLE_SEPARATOR_PATTERNS = [
  /\s+-\s+/,
  /\s+[\u2013\u2014]\s+/,
  /\s+--\s+/,
  /\s+\|\s+/,
  /:\s+/,
  /\.\s+/,
  /\?\s+/,
  /!\s+/,
] as const;

export function conciseCrawlerHeading(
  heading: string,
  fallback?: string | null,
  maxLength = DEFAULT_CRAWLER_HEADING_LENGTH,
): string {
  const normalizedHeading = normalizeHeadingText(heading);

  if (headingLength(normalizedHeading) <= maxLength) {
    return normalizedHeading;
  }

  for (const candidate of derivedHeadingCandidates(normalizedHeading)) {
    if (isUsefulHeadingCandidate(candidate, maxLength, MIN_DERIVED_HEADING_LENGTH)) {
      return candidate;
    }
  }

  const normalizedFallback = normalizeHeadingText(fallback ?? "");

  if (isUsefulHeadingCandidate(normalizedFallback, maxLength, MIN_FALLBACK_HEADING_LENGTH)) {
    return normalizedFallback;
  }

  return truncateHeading(normalizedHeading, maxLength);
}

export function fullHeadingLabel(fullHeading: string, renderedHeading: string): string | undefined {
  normalizeHeadingText(fullHeading);
  normalizeHeadingText(renderedHeading);

  return undefined;
}

function derivedHeadingCandidates(heading: string): string[] {
  const candidates: string[] = [];

  for (const pattern of TITLE_SEPARATOR_PATTERNS) {
    const [prefix] = heading.split(pattern);
    candidates.push(prefix);
  }

  for (const pattern of CLAUSE_BOUNDARY_PATTERNS) {
    const [prefix] = heading.split(pattern);
    candidates.push(prefix);
  }

  return candidates.map(normalizeHeadingText);
}

function truncateHeading(heading: string, maxLength: number): string {
  const characters = Array.from(heading);

  if (characters.length <= maxLength) {
    return heading;
  }

  const hardLimit = characters.slice(0, maxLength).join("").trim();
  const lastSpaceIndex = hardLimit.lastIndexOf(" ");

  if (lastSpaceIndex >= MIN_DERIVED_HEADING_LENGTH) {
    return hardLimit.slice(0, lastSpaceIndex).trim();
  }

  return hardLimit;
}

function isUsefulHeadingCandidate(candidate: string, maxLength: number, minLength: number): boolean {
  const length = headingLength(candidate);

  return length >= minLength && length <= maxLength;
}

function headingLength(value: string): number {
  return Array.from(value).length;
}

function normalizeHeadingText(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}
